/**
 * План периода — по его датам, а не по календарному месяцу.
 *
 * Месячный план (`computePlan`) отвечает за календарный месяц: платежи
 * месяца, весь доход источника, обычные траты целиком. Для периода от
 * зарплаты до зарплаты это неверно: страховка 2 октября попадает в
 * сентябрьский период, а не в октябрьский; у недельной няни в периоде может
 * быть пять наступлений, а не четыре. Закрепить такие суммы основой
 * сравнения значило бы сравнивать правильные факты с неверным планом.
 *
 * Поэтому каждая строка считается по датам срока плана:
 *
 * · **доход** — собственный доход периода (`ownIncomeOf`): та зарплата или
 *   выплата себе, что его открыла, по её связи, а не по датам расписания
 *   внутри срока, — плюс наступления остальных источников в сроке (аванс);
 *   у «когда как» — месячная сумма по доле дней. Выплата себе — не заработок,
 *   а деньги из своего запаса;
 * · **обязательные платежи** — наступления с датами внутри срока;
 * · **обычные траты и обязательные взносы** (подушка, минимальные платежи
 *   по долгам, резервы) — месячные суммы по доле дней каждого затронутого
 *   месяца;
 * · **остаток** делится между свободными тратами и накоплениями по режиму,
 *   как и в месячном плане.
 *
 * Срок плана — от начала периода до ожидаемого конца. Задержка зарплаты его
 * не удлиняет: лишние дни денег в плане не прибавляют.
 */
import { ZERO, add, clampPos, distribute, share, sub, type Money } from './money.js';
import { addDays, daysInMonth, diffDays, isAfter, isBefore, parts, ymd, type PlainDate } from './date.js';
import { conservativeIncome, hasDates, incomeOccurrences, rhythmOf } from './income.js';
import { obligationsInPeriod } from './instances.js';
import { MODE_SPLIT } from './mode.js';
import { computePlan, type PlanInput } from './plan.js';
import { payoutOccurrences, type StableBudgetPlan } from './stable.js';
import type { PlanLineKind } from './budget.js';

/**
 * Сколько месяцев в отрезке — по доле дней каждого месяца.
 *
 * С 5 октября по 4 ноября — 27 дней из 31 октябрьского и 4 из 30 ноябрьских:
 * чуть больше одного месяца. Делить на «среднюю длину месяца» нельзя:
 * февральский период тогда получал бы меньше, чем месяц, который он и есть.
 */
export function monthShare(from: PlainDate, to: PlainDate): number {
  if (isAfter(from, to)) return 0;
  let total = 0;
  let cursor = from;
  while (!isAfter(cursor, to)) {
    const { year, month } = parts(cursor);
    const len = daysInMonth(year, month);
    const monthEnd = ymd(year, month, len);
    const end = isBefore(monthEnd, to) ? monthEnd : to;
    total += (diffDays(cursor, end) + 1) / len;
    cursor = addDays(end, 1);
  }
  return total;
}

export interface PeriodPlanInput extends PlanInput {
  /** Срок плана: от начала периода до ожидаемого конца. */
  term: { from: PlainDate; to: PlainDate };
  /** План стабильного бюджета, если период — от выплаты до выплаты. */
  stablePlan?: StableBudgetPlan | null;
  /**
   * Собственный доход периода (`ownIncomeOf`) и его источник.
   *
   * Источник из подсчёта наступлений исключается: следующая зарплата
   * принадлежит следующему периоду, даже если задержка дотянула этот до её
   * даты, а досрочная или запоздавшая — своему.
   */
  own?: { streamId?: string; amount: Money } | null;
  /**
   * Наступления других доходов, деньги за которые пришли ещё до срока:
   * `${streamId}:${дата}`. Их деньги — в фактах прошлого периода, и второй
   * раз план их не ждёт (подработка за 6 ноября, пришедшая 1 ноября).
   */
  receivedBefore?: ReadonlySet<string>;
}

/** Ожидаемый доход срока: наступления источников, а не месячная сумма. */
function incomeIn(input: PeriodPlanInput, k: number): Money {
  const { from, to } = input.term;
  if (input.stablePlan) {
    // Живут на выплаты себе: они и есть доход периода в плане. Заработок
    // уходит в запас и доходом периода не считается.
    return input.own?.amount
      ?? share(input.stablePlan.payout, payoutOccurrences(input.stablePlan, from, to).length);
  }
  const own = input.own;
  return add(own?.amount ?? ZERO, ...input.incomeStreams.filter((s) => s.isActive && s.id !== own?.streamId).map((s) => {
    const amount = conservativeIncome(s);
    if (hasDates(rhythmOf(s))) {
      const due = incomeOccurrences(s, from, to).filter((d) => !input.receivedBefore?.has(`${s.id}:${d}`));
      return share(amount, due.length);
    }
    // «Когда как»: дат нет, сумма названа на месяц — по доле дней.
    return rhythmOf(s).kind === 'irregular' ? share(amount, k) : ZERO;
  }));
}

/**
 * Суммы строк плана за срок периода.
 *
 * Обязательный минимум взносов (подушка, минимальные платежи по долгам,
 * резервы под будущие счета) берётся из месячного плана и делится по дням:
 * правила его расчёта живут там, и второй копии им не нужно.
 */
export function periodPlanLines(input: PeriodPlanInput): Record<PlanLineKind, Money> {
  const { from, to } = input.term;
  const k = monthShare(from, to);
  const monthly = computePlan(input);

  const income = incomeIn(input, k);
  const obligations = obligationsInPeriod(input.obligations, input.today, from, to);
  const need = share(input.needBaseline, k);
  const mustFloor = share(monthly.mustFloor, k);

  const surplus = clampPos(sub(income, add(obligations, need, mustFloor)));
  const split = MODE_SPLIT[input.mode];
  const [want, saveExtra] = distribute(surplus, [split.self, split.goals]) as [Money, Money];
  const goals = add(mustFloor, saveExtra);

  return {
    income,
    obligations,
    need,
    want,
    goals,
    free: clampPos(sub(income, add(obligations, need, want, goals))),
  };
}
